import type { IUserInfo } from "@/account/account-models";
import { EUserType } from "@/account/account-models";
import type { IFeedInfo } from "@/feed/feed-models";
import type { IDictionary, IPopupConfig } from "@/models";
import { removeDuplicates } from "@/utils/array";
import type { IGetRequest } from "@/utils/async";
import { ERequestStatus, requestInit } from "@/utils/async";
import { defined } from "@/utils/variable-evaluation";
import { createReducer } from "@reduxjs/toolkit";
import { IDiscoverActions } from "./discover-actions";
import type { IDiscoverFilter, IGetProfilesConfig } from "./discover-helpers";
import { getDefaultDiscoverFiltersState } from "./discover-helpers";
import type { IDiscoverAlerts } from "./discover-models";
import { EDiscoverPunishmentAlertType } from "./discover-models";

// Extended interface for discover cards with profile feed data
export interface IDiscoverCardWithFeed extends IUserInfo {
  __isImpressionTracked?: boolean;
  profileFeed?: IFeedInfo[];
  nextProfileFeedUrl?: string;
}

export interface IDiscoverModel {
  isInitialRequest: boolean;
  filtersSet: IDictionary<IDiscoverFilter>;
  getProfiles: IGetRequest<any>;
  profiles: (IUserInfo & { __isImpressionTracked?: boolean })[];
  nextProfilesUrl: string | null;
  rank: number | null;
  alerts: IDiscoverAlerts;
  discoverPopup: IPopupConfig | null;
  getDiscoverCards: IGetRequest<any>;
  discoverCards: IDiscoverCardWithFeed[];
  getMarkCardAsVisited: IGetRequest<any>;
  getCardProfileFeed: IGetRequest<any>;
  getCardProfileFeedNext: IGetRequest<any>;
  discoverCardsHistory: IDiscoverCardWithFeed[];
}

const discoverStoreInitialState: IDiscoverModel = {
  isInitialRequest: true,
  filtersSet: getDefaultDiscoverFiltersState(),
  getProfiles: requestInit(),
  profiles: [],
  nextProfilesUrl: null,
  rank: null,
  alerts: {
    [EDiscoverPunishmentAlertType.Punishment]: false,
    [EDiscoverPunishmentAlertType.FinalPunishment]: false,
  },
  discoverPopup: null,
  getDiscoverCards: requestInit(),
  discoverCards: [],
  getMarkCardAsVisited: requestInit(),
  getCardProfileFeed: requestInit(),
  getCardProfileFeedNext: requestInit(),
  discoverCardsHistory: [],
};

/** Reducer */
export const discoverReducer = createReducer(
  discoverStoreInitialState,
  (builder) => {
    builder
      .addCase(
        IDiscoverActions.initGetProfiles,
        (state, action): IDiscoverModel => {
          const { isInitialRequest, isForceLoader } = action.payload
            .config as IGetProfilesConfig;
          return {
            ...state,
            getProfiles: {
              status: ERequestStatus.Fetching,
            },
            isInitialRequest,
            // listScrollY: isInitialRequest ? 0 : state.listScrollY,
            // profiles: isInitialRequest ? [] : state.profiles,
            profiles: isForceLoader ? [] : state.profiles,
            discoverCards: isInitialRequest ? [] : state.discoverCards,
            discoverCardsHistory: isInitialRequest
              ? []
              : state.discoverCardsHistory,
          };
        }
      )
      .addCase(
        IDiscoverActions.successGetProfiles,
        (state, action): IDiscoverModel => {
          const { config, res }: { config: IGetProfilesConfig; res: any } =
            action.payload;
          let profiles;
          if (config.isInitialRequest) {
            profiles = res.data;
          } else {
            profiles = [...state.profiles, ...res.data];
          }
          profiles = removeDuplicates(profiles, "username");
          return {
            ...state,
            nextProfilesUrl: defined(res.next) ? res.next : null,
            getProfiles: {
              responseData: action.payload,
              status: ERequestStatus.Fetched,
            },
            profiles,
            alerts: {
              [EDiscoverPunishmentAlertType.Punishment]: res.punishment_alert,
              [EDiscoverPunishmentAlertType.FinalPunishment]:
                res.final_punishment_alert,
            },
            rank: res.rank,
            filtersSet: config.filtersSet,
          };
        }
      )
      .addCase(
        IDiscoverActions.failureGetProfiles,
        (state, action: any): IDiscoverModel => {
          return {
            ...state,
            getProfiles: {
              error: action.payload,
              status: ERequestStatus.Error,
            },
          };
        }
      )
      .addCase(
        IDiscoverActions.setDiscoverPopup,
        (state, action): IDiscoverModel => {
          return {
            ...state,
            discoverPopup: action.payload,
          };
        }
      )
      .addCase(
        IDiscoverActions.setDiscoverAlertConfirmed,
        (state, action): IDiscoverModel => {
          return {
            ...state,
            alerts: {
              ...state.alerts,
              [action.payload.type]: false,
            },
          };
        }
      )
      .addCase(
        IDiscoverActions.setDiscoverProfileImpressionTracked,
        (state, action): IDiscoverModel => {
          return {
            ...state,
            profiles: state.profiles.map((profile) => {
              if (profile.username === action.payload) {
                return {
                  ...profile,
                  __isImpressionTracked: true,
                };
              }
              return profile;
            }),
          };
        }
      )
      .addCase(
        IDiscoverActions.initGetDiscoverCards,
        (state): IDiscoverModel => {
          return {
            ...state,
            getDiscoverCards: {
              status: ERequestStatus.Fetching,
            },
          };
        }
      )
      .addCase(
        IDiscoverActions.successGetDiscoverCards,
        (state, action): IDiscoverModel => {
          return {
            ...state,
            getDiscoverCards: {
              responseData: action.payload,
              status: ERequestStatus.Fetched,
            },
            discoverCards: [
              ...state.discoverCards,
              ...(action.payload.data || []).filter(
                (c: IUserInfo) =>
                  c.type === EUserType.Featured &&
                  !c.is_followee &&
                  !state.discoverCardsHistory.find(
                    (historyCard) => historyCard._id === c._id
                  ) &&
                  !state.discoverCards.find((card) => card._id === c._id)
              ),
            ],
          };
        }
      )
      .addCase(
        IDiscoverActions.failureGetDiscoverCards,
        (state, action: any): IDiscoverModel => {
          return {
            ...state,
            getDiscoverCards: {
              error: action.payload,
              status: ERequestStatus.Error,
            },
          };
        }
      )
      .addCase(
        IDiscoverActions.initGetMarkCardAsVisited,
        (state): IDiscoverModel => {
          return {
            ...state,
            discoverCards: state.discoverCards.filter((c, i) => i !== 0),
            discoverCardsHistory: [
              ...state.discoverCardsHistory,
              state.discoverCards[0],
            ],
            getMarkCardAsVisited: {
              status: ERequestStatus.Fetching,
            },
          };
        }
      )
      .addCase(
        IDiscoverActions.successGetMarkCardAsVisited,
        (state, action): IDiscoverModel => {
          return {
            ...state,
            getMarkCardAsVisited: {
              responseData: action.payload,
              status: ERequestStatus.Fetched,
            },
          };
        }
      )
      .addCase(
        IDiscoverActions.failureGetMarkCardAsVisited,
        (state, action): IDiscoverModel => {
          return {
            ...state,
            getMarkCardAsVisited: {
              error: action.payload,
              status: ERequestStatus.Error,
            },
          };
        }
      )
      .addCase(
        IDiscoverActions.setDiscoverCardImpressionTracked,
        (state, action): IDiscoverModel => {
          return {
            ...state,
            discoverCards: state.discoverCards.map((card) => {
              if (card.username === action.payload) {
                return {
                  ...card,
                  __isImpressionTracked: true,
                };
              }
              return card;
            }),
          };
        }
      )
      .addCase(
        IDiscoverActions.initGetCardProfileFeed,
        (state): IDiscoverModel => {
          return {
            ...state,
            getCardProfileFeed: {
              status: ERequestStatus.Fetching,
            },
          };
        }
      )
      .addCase(
        IDiscoverActions.successGetCardProfileFeed,
        (state, action): IDiscoverModel => {
          const { userId, profileFeed, nextProfileFeedUrl } = action.payload;
          return {
            ...state,
            getCardProfileFeed: {
              responseData: action.payload,
              status: ERequestStatus.Fetched,
            },
            discoverCards: state.discoverCards.map((card) => {
              if (card._id === userId) {
                return {
                  ...card,
                  profileFeed,
                  nextProfileFeedUrl,
                };
              }
              return card;
            }),
          };
        }
      )
      .addCase(
        IDiscoverActions.failureGetCardProfileFeed,
        (state, action: any): IDiscoverModel => {
          return {
            ...state,
            getCardProfileFeed: {
              error: action.payload,
              status: ERequestStatus.Error,
            },
          };
        }
      )
      .addCase(
        IDiscoverActions.initGetCardProfileFeedNext,
        (state): IDiscoverModel => {
          return {
            ...state,
            getCardProfileFeedNext: {
              status: ERequestStatus.Fetching,
            },
          };
        }
      )
      .addCase(
        IDiscoverActions.successGetCardProfileFeedNext,
        (state, action): IDiscoverModel => {
          const { userId, profileFeed, nextProfileFeedUrl } = action.payload;
          return {
            ...state,
            getCardProfileFeedNext: {
              responseData: action.payload,
              status: ERequestStatus.Fetched,
            },
            discoverCards: state.discoverCards.map((card) => {
              if (card._id === userId) {
                const existingFeed = card.profileFeed || [];
                const newFeed = [...existingFeed, ...profileFeed];
                return {
                  ...card,
                  profileFeed: newFeed,
                  nextProfileFeedUrl,
                };
              }
              return card;
            }),
          };
        }
      )
      .addCase(
        IDiscoverActions.failureGetCardProfileFeedNext,
        (state, action: any): IDiscoverModel => {
          return {
            ...state,
            getCardProfileFeedNext: {
              error: action.payload,
              status: ERequestStatus.Error,
            },
          };
        }
      );
  }
);
